import { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router';

/**
 * Custom hook to manage unsaved changes state and provide navigation blocking
 * Works across tab changes, asset changes, and URL navigation
 */
const useUnsavedChanges = (hasUnsavedChanges = false) => {
    const history = useHistory();
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [nextLocation, setNextLocation] = useState(null);
    const unblockRef = useRef(null);

    // Register/unregister navigation blocking based on unsaved changes
    useEffect(() => {
        if (hasUnsavedChanges) {
            // Block browser navigation (back/forward/refresh)
            const handleBeforeUnload = (event) => {
                event.preventDefault();
                event.returnValue = '';
            };

            // Block React Router navigation
            unblockRef.current = history.block((location) => {
                if (hasUnsavedChanges) {
                    setNextLocation(location);
                    setShowUnsavedModal(true);
                    return false;
                }
                return true;
            });

            window.addEventListener('beforeunload', handleBeforeUnload);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                if (unblockRef.current) {
                    unblockRef.current();
                }
            };
        }
    }, [hasUnsavedChanges, history]);

    /**
     * Check for unsaved changes before executing an action
     * @param {Function} action - The action to execute if no unsaved changes or after confirmation
     * @param {Object} actionData - Optional data to pass to the action
     * @returns {boolean} - True if action can proceed, false if blocked by unsaved changes
     */
    const checkUnsavedChanges = useCallback((action, actionData = null) => {
        if (hasUnsavedChanges) {
            setPendingAction(() => () => action(actionData));
            setShowUnsavedModal(true);
            return false;
        } else {
            action(actionData);
            return true;
        }
    }, [hasUnsavedChanges]);

    /**
     * Handle user confirmation to discard changes and continue with pending action
     */
    const handleDiscardAndContinue = useCallback(() => {
        setShowUnsavedModal(false);

        // Execute pending action if exists
        if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }

        // Navigate to next location if exists (for URL navigation)
        if (nextLocation) {
            if (unblockRef.current) {
                unblockRef.current();
            }
            history.push(`${nextLocation.pathname}${nextLocation.search}`);
            setNextLocation(null);
        }
    }, [pendingAction, nextLocation, history]);

    /**
     * Handle user cancellation - stay on current page/state
     */
    const handleStayOnPage = useCallback(() => {
        setShowUnsavedModal(false);
        setPendingAction(null);
        setNextLocation(null);
    }, []);

    return {
        showUnsavedModal,
        setShowUnsavedModal,
        checkUnsavedChanges,
        handleDiscardAndContinue,
        handleStayOnPage
    };
};

export default useUnsavedChanges;
